import React, { useState } from 'react';
import { Edit2, Info, HeartPulse, Sparkles, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
const pulseScoreData = [{
  month: 'Apr',
  score: 68,
  isKeyEvent: false
}, {
  month: 'May',
  score: 55,
  event: 'Private financials show declining margins',
  isKeyEvent: true
}, {
  month: 'Jun',
  score: 42,
  event: 'CFO and VP Sales departed (LinkedIn signal)',
  isKeyEvent: true
}, {
  month: 'Jul',
  score: 28,
  event: 'Website down for 5+ days — operational risk',
  isKeyEvent: true
}, {
  month: 'Aug',
  score: 38,
  isKeyEvent: false
}, {
  month: 'Sep',
  score: 51,
  event: 'Credit limit decreased by trade creditor',
  isKeyEvent: true
}, {
  month: 'Oct',
  score: 60,
  isKeyEvent: false
}, {
  month: 'Nov',
  score: 65,
  event: 'Sales team grew +4 hires (LinkedIn signal)',
  isKeyEvent: true
}, {
  month: 'Dec',
  score: 58,
  event: 'DBT increased 67% MoM (6→10 days)',
  isKeyEvent: true
}, {
  month: 'Jan',
  score: 72,
  event: 'Private financials show revenue rebound',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 78,
  isKeyEvent: false
}, {
  month: 'Mar',
  score: 72,
  event: 'DBT ticking up — monitoring',
  isKeyEvent: true
}];
const healthScoreData = [{
  month: 'Apr',
  score: 86
}, {
  month: 'May',
  score: 87
}, {
  month: 'Jun',
  score: 86
}, {
  month: 'Jul',
  score: 88
}, {
  month: 'Aug',
  score: 87
}, {
  month: 'Sep',
  score: 88,
  event: 'Strong Z-Score improvement (2.9→3.4)',
  isKeyEvent: true
}, {
  month: 'Oct',
  score: 89
}, {
  month: 'Nov',
  score: 88
}, {
  month: 'Dec',
  score: 89
}, {
  month: 'Jan',
  score: 90,
  event: 'Debt-to-equity ratio decreased to 0.8x',
  isKeyEvent: true
}, {
  month: 'Feb',
  score: 89
}, {
  month: 'Mar',
  score: 88
}];
const paymentScoreData = [{
  month: 'Apr',
  score: 74
}, {
  month: 'May',
  score: 73
}, {
  month: 'Jun',
  score: 72
}, {
  month: 'Jul',
  score: 74
}, {
  month: 'Aug',
  score: 72
}, {
  month: 'Sep',
  score: 70
}, {
  month: 'Oct',
  score: 69,
  event: 'DBT increased from 6 to 10 days',
  isKeyEvent: true
}, {
  month: 'Nov',
  score: 67
}, {
  month: 'Dec',
  score: 66
}, {
  month: 'Jan',
  score: 67
}, {
  month: 'Feb',
  score: 69,
  event: 'Average 60+ day balance ratio improved',
  isKeyEvent: true
}, {
  month: 'Mar',
  score: 68
}];
function PositiveIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-1541">
      <div className="w-1 h-1.5 bg-green-500 rounded-sm" data-id="element-1542" />
      <div className="w-1 h-2.5 bg-green-500 rounded-sm" data-id="element-1543" />
      <div className="w-1 h-3.5 bg-green-500 rounded-sm" data-id="element-1544" />
    </div>;
}
function NegativeIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-1545">
      <div className="w-1 h-1.5 bg-red-500 rounded-sm" data-id="element-1546" />
      <div className="w-1 h-2.5 bg-red-500 rounded-sm" data-id="element-1547" />
      <div className="w-1 h-3.5 bg-red-500 rounded-sm" data-id="element-1548" />
    </div>;
}
function ScoreTooltip({
  active,
  payload,
  label,
  color
}: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dotColor = data.score !== undefined ? getDotColor(data.score) : color;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-3 max-w-[250px]" data-id="element-1549">
        <div className="flex items-center justify-between mb-2" data-id="element-1550">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1551">
            {label}
          </p>
          <div className="flex items-center gap-1.5" data-id="element-1552">
            <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: dotColor
          }} data-id="element-1553" />
            <span className="text-sm font-bold text-gray-900" data-id="element-1554">
              {payload[0].value}
            </span>
          </div>
        </div>
        {data.event && <p className="text-sm text-gray-700 leading-snug border-t border-gray-100 pt-2 mt-1" data-id="element-1555">
            {data.event}
          </p>}
      </div>;
  }
  return null;
}
function getDotColor(score: number) {
  if (score <= 30) return '#ef4444';
  if (score <= 70) return '#eab308';
  return '#22c55e';
}
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  if (payload.isKeyEvent) {
    return <g data-id="element-1556">
        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.2} className="animate-pulse" data-id="element-1557" />
        <circle cx={cx} cy={cy} r={6} fill="#ffffff" stroke={color} strokeWidth={3} data-id="element-1558" />
      </g>;
  }
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={2} data-id="element-1559" />;
};
const CustomActiveDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  return <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} data-id="element-1560" />;
};
export function OverviewTab() {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1561">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1562">
        {/* Credit Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-1" data-id="element-1563">
          <div className="flex justify-between items-center mb-6" data-id="element-1564">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-1565">Credit Settings</h3>
            <button className="w-8 h-8 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center text-white transition-colors" data-id="element-1566">
              <Edit2 className="w-4 h-4" data-id="element-1567" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-5 gap-x-4" data-id="element-1568">
            <div data-id="element-1569">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1570">
                Credit Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-1571">$50,000</p>
            </div>
            <div data-id="element-1572">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1573">
                Recommended Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-1574">$50,000</p>
            </div>
            <div data-id="element-1575">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1576">
                Outstanding Balance
              </p>
              <p className="text-sm text-gray-600" data-id="element-1577">$12,450</p>
            </div>
            <div data-id="element-1578">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1579">
                AP Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-1580">Angela Martin</p>
            </div>
            <div data-id="element-1581">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1582">
                Terms
              </p>
              <p className="text-sm text-gray-600" data-id="element-1583">Net 30</p>
            </div>
            <div data-id="element-1584">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1585">
                Purchasing Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-1586">Dwight Schrute</p>
            </div>
            <div data-id="element-1587">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1588">
                Late Pay Penalty
              </p>
              <p className="text-sm text-gray-600" data-id="element-1589">1.5%</p>
            </div>
            <div data-id="element-1590">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1591">
                Company Officer
              </p>
              <p className="text-sm text-gray-600" data-id="element-1592">Michael Scott</p>
            </div>
            <div data-id="element-1593">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1594">
                Date Last Approved
              </p>
              <p className="text-sm text-gray-600" data-id="element-1595">2/20/2026</p>
            </div>
            <div data-id="element-1596">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-1597">
                Group
              </p>
              <p className="text-sm text-gray-600" data-id="element-1598">Paper & Office Supplies</p>
            </div>
          </div>
        </div>

        {/* Pulse Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-2 flex flex-col" data-id="element-1599">
          <div className="flex items-center gap-2 mb-4" data-id="element-1600">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-1601">Pulse Score</h3>
            <Info className="w-4 h-4 text-gray-400 cursor-help" data-id="element-1602" />
          </div>
          <div className="flex gap-6 mb-6" data-id="element-1603">
            <div className="bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center min-w-[120px] shrink-0" data-id="element-1604">
              <span className="text-4xl font-bold text-gray-900" data-id="element-1605">72</span>
              <span className="text-sm font-medium text-gray-800 mt-1" data-id="element-1606">
                Low Risk
              </span>
            </div>
            <p className="text-gray-600 text-base leading-relaxed pt-2" data-id="element-1607">
              DUNDER MIFFLIN PAPER COMPANY has an overall Pulse Score of 72,
              indicating low risk. Their score has shown significant volatility
              over the past year but recently stabilized in the healthy range,
              despite a slight dip this month due to increased DBT.
            </p>
          </div>
          <div className="flex-1 min-h-[200px] relative" data-id="element-1608">
            <div className="absolute top-0 right-0 flex items-center gap-2 text-xs text-gray-500 z-10" data-id="element-1609">
              <div className="w-3 h-3 bg-black rounded-sm" data-id="element-1610"></div>
              Pulse Score
            </div>
            <ResponsiveContainer width="100%" height="100%" data-id="element-1611">
              <LineChart data={pulseScoreData} margin={{
              top: 20,
              right: 10,
              left: -20,
              bottom: 10
            }} data-id="element-1612">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1613" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} dy={10} data-id="element-1614" />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-1615" />
                <Tooltip content={<ScoreTooltip data-id="element-1617" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2,
                strokeDasharray: '4 4'
              }} data-id="element-1616" />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" data-id="element-1618" />
                <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="4 4" data-id="element-1619" />
                <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-1621" />} activeDot={<CustomActiveDot data-id="element-1622" />} data-id="element-1620" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1623">
        <button onClick={() => setHealthExpanded(!healthExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-1624">
          <div className="flex items-center gap-3" data-id="element-1625">
            <HeartPulse className="w-5 h-5 text-gray-700" data-id="element-1626" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-1627">Health Score</h3>
            <Info className="w-4 h-4 text-gray-400" data-id="element-1628" />
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-green-500 bg-white ml-2" data-id="element-1629">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" data-id="element-1630"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-1631">88</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-1632" />
          </div>
          {healthExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1633" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1634" />}
        </button>

        {healthExpanded && <div className="p-5" data-id="element-1635">
            <p className="text-base text-gray-600 mb-6" data-id="element-1636">
              DUNDER MIFFLIN PAPER COMPANY has a 1.2% probability of insolvency
              or bankruptcy in the next 12 months.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1637">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1638">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-1639">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-1640">
                  <div className="flex items-start gap-2" data-id="element-1641">
                    <NegativeIndicator data-id="element-1642" />
                    <span className="text-sm text-gray-600" data-id="element-1643">
                      Recent DBT increase trend (67% MoM)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1644">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-1645">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-1646">
                  <div className="flex items-start gap-2" data-id="element-1647">
                    <PositiveIndicator data-id="element-1648" />
                    <span className="text-sm text-gray-600" data-id="element-1649">
                      Years in business (77 years)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1650">
                    <PositiveIndicator data-id="element-1651" />
                    <span className="text-sm text-gray-600" data-id="element-1652">
                      Average monthly revenue over twelve months
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1653">
                    <PositiveIndicator data-id="element-1654" />
                    <span className="text-sm text-gray-600" data-id="element-1655">
                      Strong Z-Score (3.42) and low debt-to-equity
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1656">
                    <PositiveIndicator data-id="element-1657" />
                    <span className="text-sm text-gray-600" data-id="element-1658">
                      Stable employee headcount trend
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-1659">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1660">
                  <LineChart data={healthScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-1661">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1662" />
                    <XAxis dataKey="month" hide data-id="element-1663" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-1664" />
                    <Tooltip content={<ScoreTooltip color="#22c55e" data-id="element-1666" />} data-id="element-1665" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-1668" />} activeDot={<CustomActiveDot data-id="element-1669" />} data-id="element-1667" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1670">
        <button onClick={() => setPaymentExpanded(!paymentExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-1671">
          <div className="flex items-center gap-3" data-id="element-1672">
            <CreditCard className="w-5 h-5 text-gray-700" data-id="element-1673" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-1674">Payment Score</h3>
            <Info className="w-4 h-4 text-gray-400" data-id="element-1675" />
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-yellow-500 bg-white ml-2" data-id="element-1676">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" data-id="element-1677"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-1678">68</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-1679" />
          </div>
          {paymentExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1680" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1681" />}
        </button>

        {paymentExpanded && <div className="p-5" data-id="element-1682">
            <p className="text-base text-gray-600 mb-6" data-id="element-1683">
              DUNDER MIFFLIN PAPER COMPANY has a 12.3% probability of late
              payment, indicating moderate risk.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1684">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1685">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-1686">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-1687">
                  <div className="flex items-start gap-2" data-id="element-1688">
                    <NegativeIndicator data-id="element-1689" />
                    <span className="text-sm text-gray-600" data-id="element-1690">
                      Average 60+ day overdue balance ratio
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1691">
                    <NegativeIndicator data-id="element-1692" />
                    <span className="text-sm text-gray-600" data-id="element-1693">
                      Recent DBT increase (6 to 10 days)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-1694">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-1695">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-1696">
                  <div className="flex items-start gap-2" data-id="element-1697">
                    <PositiveIndicator data-id="element-1698" />
                    <span className="text-sm text-gray-600" data-id="element-1699">
                      DBT well below industry average
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1700">
                    <PositiveIndicator data-id="element-1701" />
                    <span className="text-sm text-gray-600" data-id="element-1702">
                      Your AR consistently 2-4 days
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-1703">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1704">
                  <LineChart data={paymentScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-1705">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1706" />
                    <XAxis dataKey="month" hide data-id="element-1707" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-1708" />
                    <Tooltip content={<ScoreTooltip color="#eab308" data-id="element-1710" />} data-id="element-1709" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-1712" />} activeDot={<CustomActiveDot data-id="element-1713" />} data-id="element-1711" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}